import { useRef, useState } from 'react';
import axios from 'axios';
import { buildMeetingInfo, meetingLogNote } from '../utils/meetingInfo.js';

// One-click "Copy meeting info" button for LeadDetail / CustomerDetail.
// Composes the 3-line block via buildMeetingInfo() and writes it to the
// clipboard. Shows a 1.5s success state ("Copied!") so the user has
// visible feedback that it worked. Falls back to a textarea + execCommand
// for older browsers / non-HTTPS contexts where navigator.clipboard
// isn't available.
// After a successful copy it also logs a Demo (lead) / Meeting (customer)
// note on the record, once per page visit, and calls onLogged so the page
// can refresh its notes.
export default function CopyMeetingInfoButton({ record, user, kind, contactId, onLogged, className = '' }) {
  const [copied, setCopied] = useState(false);
  const logged = useRef(false);
  const [err, setErr] = useState(null);

  async function handleClick() {
    const text = buildMeetingInfo(record, user, { kind });
    if (!text) { setErr('Nothing to copy'); setTimeout(() => setErr(null), 1500); return; }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Legacy fallback. document.execCommand is deprecated but still
        // works on insecure-origin/older browsers.
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      const id = Number(contactId || record?.id);
      if (id && !logged.current) {
        logged.current = true;
        try {
          const n = meetingLogNote(record, { kind });
          await axios.post('/api/notes', { contact_id: id, type: n.type, text: n.text });
          if (typeof onLogged === 'function') onLogged();
        } catch (le) {
          logged.current = false; // copying worked; the note can be retried on the next click
          console.error('Meeting note log failed:', le);
        }
      }
    } catch (e) {
      console.error('Copy meeting info failed:', e);
      setErr('Copy failed');
      setTimeout(() => setErr(null), 2000);
    }
  }

  const label = err ? err : (copied ? 'Copied!' : 'Copy meeting info');
  return (
    <button
      type="button"
      className={`btn-secondary ${className}`.trim()}
      onClick={handleClick}
      title="Copy meeting info for a Teams/Zoom invite: title, purpose, site, contact and host"
    >
      {label}
    </button>
  );
}
